import AnimatedLoader from '@/../img/animated-loader.svg';
import React, { useCallback } from 'react';
import { SelectPicker } from 'rsuite';
import styled, { css } from 'styled-components';
import { InFlightValue } from './in-flight-value';
import { InteractiveCellContainer } from './interactive-cell-container';

// Using an overlay to display the loader and block interactions during
// an update is harder, since SelectPicker applies its own z-index.
const inFlightSelectPickerStyle = css`
  opacity: 0.3;
  pointer-events: none;
`;

const StyledSelectPicker = styled(SelectPicker)`
  width: 100%;
  &.rs-picker-select .rs-picker-toggle * {
    font-size: 14px;
    &.rs-picker-toggle-value {
      color: #000;
      color: var(--ag-data-color, var(--ag-foreground-color, #000));
    }
  }
`;

const PickerContainer = styled(InteractiveCellContainer)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const loaderSize = 24;

const UpdatingAnimatedLoader = styled(AnimatedLoader)`
  position: absolute;
`;

/**
 * ag-grid custom cell renderer that allows editing a cell via a dropdown picker
 * without having to click/double-click on the cell first to go into ag-grid's "edit" mode
 *
 * @param {any} props.value Current cell value. Passed by ag-grid
 * @param {RowNode} props.node Row node of the row containing this cell. Passed by ag-grid
 * @param {ColDef} props.colDef ColDef that corresponds to the cell. Passed by ag-grid
 * @param {(e: EditableCellChangeEvent) => void} props.onChange Callback
 * invoked when user picks a new value
 * @param {{ label: string, value: any}[]} props.dropdownOptions Array of options selectable
 * from the dropdown
 * @param {Partial<SelectPickerProps> | undefined} props.dropdownProps Additional props
 * to be passed down to the underlying dropdown component, `SelectPicker`. Optional.
 * @returns {JSX.Element}
 */
export function EditableDropdownCell(props) {
  const {
    value: rawValue,
    node,
    onChange,
    dropdownOptions,
    dropdownProps,
    colDef,
  } = props;

  const { id: rowId } = node;

  const onChangeDropdownValue = useCallback(
    (newValue) => {
      if (!onChange) {
        return;
      }

      onChange({ rowId, cellValue: newValue, colDef });
    },
    [onChange, rowId, colDef]
  );

  // Unpack wrapped in-flight value if needed
  // Use `inFlight` to tell the user the update is still pending
  const inFlight = rawValue instanceof InFlightValue;
  const value = inFlight ? rawValue.value : rawValue;

  return (
    <PickerContainer>
      <StyledSelectPicker
        // SelectPicker throws a warning when passing unrecognized props
        // - using `css` prop instead of styled component interpolation
        css={inFlight && inFlightSelectPickerStyle}
        data={dropdownOptions}
        value={value}
        onChange={onChangeDropdownValue}
        searchable={false}
        appearance="subtle"
        size="xs"
        cleanable={false}
        {...dropdownProps}
      />
      {inFlight && (
        <UpdatingAnimatedLoader width={loaderSize} height={loaderSize} />
      )}
    </PickerContainer>
  );
}
